/**
 * Permission constants for role-based access control
 * Map frontend features/modules to backend permission names
 */

export const PERMISSIONS = {
    // Chat Permissions
    ACTIVE_CHAT: "ACTIVE_CHAT",
    PREVIOUS_CHAT: "PREVIOUS_CHAT",

    // Status Management
    UPDATE_STATUS: "UPDATE_STATUS",

    // Report Permissions
    REPORT_CONVERSATION_USER: "REPORT_CONVERSATION_USER",
    REPORT_LIVE_AGENT_ANALYTICS: "REPORT_LIVE_AGENT_ANALYTICS",
    REPORT_MODULE_ANALYTICS: "REPORT_MODULE_ANALYTICS",
    REPORT_PERFORMANCE_MATRICS: "REPORT_PERFORMANCE_MATRICS",
    ROPORT_FEEDBACK_ANALYTICS: "ROPORT_FEEDBACK_ANALYTICS",
    WEB_SCRAPING: "WEB_SCRAPING",
    REPORT_SENTIMENT_ANALYTICS: "REPORT_SENTIMENT_ANALYTICS",
    QUEUE_LIST: "QUEUE_LIST",
    AGENT_LIST: "AGENT_LIST",
    ADMIN_QUEUE_LIST: "ADMIN_QUEUE_LIST",
    HISTORY_LIST: "SUPERVISOR_AGENT_HISTORY",
    ADMIN_USER_LIST: "ADMIN_USER_LIST",
    LVE_CHAT: "LIVE_CHAT",
    AGENT_TICKET_LIST: "AGENT_TICKET_LIST",
    TICKET_MANAGEMENT: "TICKET_MANAGEMENT",
    TECH_SUPPORT_TICKET_LIST: "TECH_SUPPORT_TICKET_LIST",
    PEAK_USAGE: "PEAK_USAGE",
    KNOWLEDGE_BASE: "KNOWLEDGE_BASE"
} as const;

export type PermissionName = typeof PERMISSIONS[keyof typeof PERMISSIONS];

/**
 * Permission groups for easier management
 */
export const PERMISSION_GROUPS = {
    CHAT: [PERMISSIONS.ACTIVE_CHAT, PERMISSIONS.PREVIOUS_CHAT, PERMISSIONS.AGENT_TICKET_LIST],
    REPORTS: [
        PERMISSIONS.REPORT_CONVERSATION_USER,
        PERMISSIONS.REPORT_LIVE_AGENT_ANALYTICS,
        PERMISSIONS.REPORT_MODULE_ANALYTICS,
        PERMISSIONS.REPORT_PERFORMANCE_MATRICS,
        PERMISSIONS.ROPORT_FEEDBACK_ANALYTICS,
        PERMISSIONS.PEAK_USAGE,
    ],
    MANAGEMENT: [PERMISSIONS.QUEUE_LIST, PERMISSIONS.AGENT_LIST, PERMISSIONS.HISTORY_LIST, PERMISSIONS.LVE_CHAT, PERMISSIONS.TICKET_MANAGEMENT, PERMISSIONS.TECH_SUPPORT_TICKET_LIST],
} as const;

/**
 * Route to permission mapping
 * Maps frontend routes to required permissions
 */
export const ROUTE_PERMISSIONS = {
    "/dashboards/chat": [PERMISSIONS.ACTIVE_CHAT],
    "/dashboards/history": [PERMISSIONS.PREVIOUS_CHAT],
    "/dashboards/ticket-list": [PERMISSIONS.AGENT_TICKET_LIST],
    "/dashboards/reports/conversation-users": [PERMISSIONS.REPORT_CONVERSATION_USER],
    "/dashboards/reports/live-agent-analytics": [PERMISSIONS.REPORT_LIVE_AGENT_ANALYTICS],
    "/dashboards/reports/module-analytics": [PERMISSIONS.REPORT_MODULE_ANALYTICS],
    "/dashboards/reports/performance-metrics": [PERMISSIONS.REPORT_PERFORMANCE_MATRICS],
    "/dashboards/reports/feedback-analytics": [PERMISSIONS.ROPORT_FEEDBACK_ANALYTICS],
    "/dashboards/queues": [PERMISSIONS.QUEUE_LIST],
    "/dashboards/agents": [PERMISSIONS.AGENT_LIST],
    "/dashboards/history-list": [PERMISSIONS.HISTORY_LIST],
    "/dashboards/users": [PERMISSIONS.ADMIN_USER_LIST],
    "/dashboards/livechat": [PERMISSIONS.LVE_CHAT],
    "/dashboards/ticketing": [PERMISSIONS.TICKET_MANAGEMENT, PERMISSIONS.TECH_SUPPORT_TICKET_LIST],
    "/dashboards/reports/peak-usages": [PERMISSIONS.PEAK_USAGE],
} as const;

/**
 * Feature to permission mapping
 * Maps specific UI features to required permissions
 */
export const FEATURE_PERMISSIONS = {
    canAccessActiveChats: [PERMISSIONS.ACTIVE_CHAT],
    canAccessChatHistory: [PERMISSIONS.PREVIOUS_CHAT],
    canViewConversationUserReport: [PERMISSIONS.REPORT_CONVERSATION_USER],
    canViewLiveAgentAnalytics: [PERMISSIONS.REPORT_LIVE_AGENT_ANALYTICS],
    canViewModuleAnalytics: [PERMISSIONS.REPORT_MODULE_ANALYTICS],
    canViewPerformanceMetrics: [PERMISSIONS.REPORT_PERFORMANCE_MATRICS],
    canViewFeedbackAnalytics: [PERMISSIONS.ROPORT_FEEDBACK_ANALYTICS],
    canAccessQueues: [PERMISSIONS.QUEUE_LIST],
    canAccessAgents: [PERMISSIONS.AGENT_LIST],
    canAccessHistoryList: [PERMISSIONS.AGENT_LIST],
    canAccessUsers: [PERMISSIONS.ADMIN_USER_LIST],
    canAccessLiveChat: [PERMISSIONS.LVE_CHAT],
    canAccessTicketList: [PERMISSIONS.AGENT_TICKET_LIST],
    canAccessTicketManagement: [PERMISSIONS.TICKET_MANAGEMENT, PERMISSIONS.TECH_SUPPORT_TICKET_LIST],
    canAccessPeakUsage: [PERMISSIONS.PEAK_USAGE],
} as const;
